import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const img = (p) => p?.startsWith("http") ? p : `${BACKEND}${p}`;

export default function GuideDetail() {
    const { slug } = useParams();
    const [guide, setGuide] = useState(null);
    const [email, setEmail] = useState("");
    const [unlockedEmail, setUnlockedEmail] = useState("");
    const [unlocked, setUnlocked] = useState(false);
    const [token, setToken] = useState(null);
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => {
        api.get(`/guides/${slug}`).then(r => setGuide(r.data)).catch(() => setErr("Guide introuvable"));
        // Attempt to re-unlock using locally remembered email
        const remembered = localStorage.getItem("cp_email");
        if (remembered) {
            api.post(`/guides/unlock`, { email: remembered, guide_slug: slug })
                .then(r => {
                    if (r.data.unlocked) {
                        setUnlocked(true);
                        setToken(r.data.access_token);
                        setUnlockedEmail(remembered);
                    }
                })
                .catch(() => {});
        }
    }, [slug]);

    useEffect(() => {
        if (unlocked && token) {
            api.get(`/guides/${slug}/content/${token}`)
                .then(r => setContent(r.data.content))
                .catch(() => setErr("Impossible de charger le contenu."));
        }
    }, [unlocked, token, slug]);

    async function handleUnlock(e) {
        e.preventDefault();
        if (!email) return;
        setLoading(true); setErr("");
        try {
            const r = await api.post(`/guides/unlock`, { email, guide_slug: slug });
            if (r.data.unlocked) {
                setUnlocked(true);
                setToken(r.data.access_token);
                setUnlockedEmail(email);
                localStorage.setItem("cp_email", email);
            } else {
                setErr("Aucun achat enregistré pour cet email. Si vous venez d'acheter, patientez quelques secondes.");
            }
        } catch { setErr("Erreur — réessayez."); }
        setLoading(false);
    }

    async function handleBuy() {
        if (!email) { setErr("Merci d'indiquer votre email avant de payer."); return; }
        setLoading(true); setErr("");
        localStorage.setItem("cp_email", email);
        try {
            const r = await api.post(`/checkout/session`, {
                product_type: "guide",
                product_slug: slug,
                email,
                origin_url: window.location.origin,
            });
            window.location.href = r.data.url;
        } catch { setErr("Paiement impossible — réessayez."); setLoading(false); }
    }

    if (!guide) return <div className="min-h-screen bg-cp-ivoire flex items-center justify-center cp-serif text-cp-gris">{err || "Chargement…"}</div>;

    return (
        <div data-testid="guide-detail-page" className="bg-cp-ivoire">
            <section className="relative min-h-[60vh] bg-cp-encre text-cp-ivoire overflow-hidden cp-grain">
                <img src={img(guide.cover)} alt="" className="absolute inset-0 w-full h-full object-cover cp-kenburns-slow cp-photo-cinematic"/>
                <div className="absolute inset-0 cp-hero-overlay"/>
                <div className="relative z-10 min-h-[60vh] max-w-[1400px] mx-auto px-5 sm:px-6 md:px-12 pt-32 md:pt-36 pb-14 flex flex-col justify-end">
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}>
                        <p className="cp-tiny text-cp-sable mb-6">Guide digital · {guide.pages} pages · {guide.price}€</p>
                        <h1 className="cp-serif text-[44px] sm:text-[64px] md:text-[104px] lg:text-[144px] leading-[0.9] tracking-[-0.03em] max-w-4xl">
                            {guide.title}
                        </h1>
                        <p className="cp-serif italic text-lg md:text-2xl text-cp-ivoire/85 leading-snug mt-6 max-w-2xl">{guide.subtitle}</p>
                    </motion.div>
                </div>
            </section>

            {!unlocked ? (
                <section className="py-20 md:py-28 max-w-3xl mx-auto px-5 sm:px-6 md:px-12">
                    <div className="bg-cp-craie border border-cp-border p-8 md:p-14">
                        <p className="cp-tiny text-cp-terre mb-5">Aperçu</p>
                        <p className="cp-serif text-xl md:text-2xl leading-[1.5] text-cp-encre mb-10">{guide.teaser}</p>
                        <div className="border-t border-cp-border pt-10 space-y-5">
                            <p className="cp-serif italic text-2xl md:text-3xl text-cp-foret">Débloquer le guide — {guide.price}€</p>
                            <input
                                type="email" placeholder="votre@email.com" value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="guide-email-input"
                                className="w-full px-4 py-3 bg-cp-ivoire border border-cp-border focus:border-cp-foret outline-none cp-serif text-lg"
                            />
                            {err && <p className="cp-tiny text-cp-terre" data-testid="guide-error">{err}</p>}
                            <div className="flex flex-wrap gap-4 items-center">
                                <button onClick={handleBuy} disabled={loading} className="cp-btn-primary" data-testid="guide-buy-btn">
                                    {loading ? "Un instant..." : `Acheter — ${guide.price}€`}
                                </button>
                                <button onClick={handleUnlock} disabled={loading} className="cp-link text-cp-foret cp-tiny underline" data-testid="guide-unlock-btn">
                                    J'ai déjà acheté ce guide
                                </button>
                            </div>
                            <p className="cp-tiny text-cp-gris mt-4">Paiement 100% sécurisé via Stripe · accès permanent après achat · aucun renouvellement automatique.</p>
                        </div>
                    </div>
                </section>
            ) : (
                <section className="py-20 md:py-28 max-w-3xl mx-auto px-5 sm:px-6 md:px-12" data-testid="guide-content">
                    <div className="bg-cp-craie border border-cp-foret/30 px-6 py-4 mb-12 flex items-center justify-between gap-3 flex-wrap">
                        <p className="cp-tiny text-cp-foret">Débloqué · {unlockedEmail}</p>
                        <button onClick={() => { setUnlocked(false); setContent(null); setToken(null); }} className="cp-tiny text-cp-gris underline">Déconnexion</button>
                    </div>

                    {content ? (
                        <article className="cp-serif text-lg md:text-xl leading-[1.6] text-cp-encre space-y-10">
                            {content.intro && (
                                <p className="first-letter:cp-serif first-letter:text-[96px] first-letter:leading-[0.78] first-letter:float-left first-letter:pr-4 first-letter:pt-2 first-letter:text-cp-foret first-letter:font-normal">
                                    {content.intro}
                                </p>
                            )}
                            {content.chapters?.map((ch, i) => (
                                <div key={i} className="border-l-2 border-cp-terre pl-6 md:pl-8">
                                    <p className="cp-tiny text-cp-terre mb-2">{ch.hour}</p>
                                    <h3 className="cp-serif italic text-2xl md:text-3xl text-cp-foret mb-3">{ch.title}</h3>
                                    <p>{ch.text}</p>
                                </div>
                            ))}
                            {content.sections?.map((s, i) => (
                                <div key={i}>
                                    <h3 className="cp-serif italic text-2xl md:text-3xl text-cp-foret mb-3">{s.title}</h3>
                                    <p>{s.text}</p>
                                </div>
                            ))}
                            {content.closing && (
                                <p className="cp-serif italic text-xl text-cp-terre border-t border-cp-border pt-10">{content.closing}</p>
                            )}
                            {content.note && (
                                <p className="bg-cp-craie p-6 cp-serif italic text-cp-gris">{content.note}</p>
                            )}
                        </article>
                    ) : (
                        <p className="cp-serif italic text-cp-gris">Chargement du guide…</p>
                    )}
                </section>
            )}

            <section className="py-16 border-t border-cp-border bg-cp-ivoire">
                <div className="max-w-[1000px] mx-auto px-5 sm:px-6 md:px-12 text-center">
                    <Link to="/guides" className="cp-link text-cp-foret">← Tous les guides</Link>
                </div>
            </section>
        </div>
    );
}
